import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import { HABIT_COLORS, HABIT_ICONS } from '../lib/templates';
import * as LucideIcons from 'lucide-react';

const DAYS = [
  { v: 0, l: 'Sun' }, { v: 1, l: 'Mon' }, { v: 2, l: 'Tue' },
  { v: 3, l: 'Wed' }, { v: 4, l: 'Thu' }, { v: 5, l: 'Fri' }, { v: 6, l: 'Sat' },
];

const toIconKey = (name) =>
  (name || 'sparkles').split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('');

export const HabitFormModal = ({ open, onOpenChange, initial, onSubmit }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('binary');
  const [target, setTarget] = useState(1);
  const [unit, setUnit] = useState('');
  const [color, setColor] = useState(HABIT_COLORS[0]);
  const [icon, setIcon] = useState('sparkles');
  const [timeOfDay, setTimeOfDay] = useState('anytime');
  const [scheduleType, setScheduleType] = useState('daily');
  const [scheduleDays, setScheduleDays] = useState([1, 2, 3, 4, 5]);
  const [reminderTime, setReminderTime] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    supabase.from('categories').select('*').order('created_at').then(({ data }) => {
      setCategories(data || []);
    });
  }, [open]);

  useEffect(() => {
    if (open) {
      setName(initial?.name || '');
      setDescription(initial?.description || '');
      setType(initial?.type || 'binary');
      setTarget(initial?.target_value ?? 1);
      setUnit(initial?.unit ?? '');
      setColor(initial?.color || HABIT_COLORS[0]);
      setIcon(initial?.icon || 'sparkles');
      setTimeOfDay(initial?.time_of_day || 'anytime');
      setScheduleType(initial?.schedule_type || 'daily');
      setScheduleDays(initial?.schedule_days?.length ? initial.schedule_days : [1, 2, 3, 4, 5]);
      setReminderTime(initial?.reminder_time || '');
      setCategoryId(initial?.category_id || '');
    }
  }, [open, initial]);

  const toggleDay = (d) =>
    setScheduleDays((days) => (days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort()));

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim() || null,
        type,
        target_value: type === 'binary' ? 1 : Number(target) || 1,
        unit: type === 'time' ? 'min' : unit.trim(),
        color,
        icon,
        time_of_day: timeOfDay,
        schedule_type: scheduleType,
        schedule_days: scheduleType === 'custom' ? scheduleDays : [],
        reminder_time: reminderTime ? reminderTime : null,
        category_id: categoryId || null,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const IconPreview = LucideIcons[toIconKey(icon)] || LucideIcons.Sparkles;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="habit-form-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">{initial?.id ? 'Edit Habit' : 'New Habit'}</DialogTitle>
          <DialogDescription>Design a habit that fits your flow.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="flex items-center gap-3">
            <div
              className="h-12 w-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${color}1F`, color }}
            >
              <IconPreview className="h-6 w-6" strokeWidth={1.6} />
            </div>
            <div className="flex-1">
              <Label htmlFor="habit-name">Name</Label>
              <Input
                id="habit-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Morning meditation"
                required
                data-testid="habit-form-name"
                autoFocus
              />
            </div>
          </div>

          <div>
            <Label>Description (optional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Why does this matter to you?"
              rows={2}
              data-testid="habit-form-description"
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            {[
              { v: 'binary', l: 'Check', d: 'Yes / No' },
              { v: 'quantitative', l: 'Count', d: 'e.g. 8 glasses' },
              { v: 'time', l: 'Timer', d: 'Minutes' },
            ].map((t) => (
              <button
                type="button"
                key={t.v}
                onClick={() => setType(t.v)}
                className={`rounded-xl border p-3 text-left transition-all ${
                  type === t.v ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground'
                }`}
                data-testid={`habit-form-type-${t.v}`}
              >
                <div className="font-medium text-sm">{t.l}</div>
                <div className="text-xs text-muted-foreground">{t.d}</div>
              </button>
            ))}
          </div>

          {type !== 'binary' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Target</Label>
                <Input
                  type="number"
                  min={1}
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  data-testid="habit-form-target"
                />
              </div>
              {type === 'quantitative' && (
                <div>
                  <Label>Unit</Label>
                  <Input
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    placeholder="glasses, pages..."
                    data-testid="habit-form-unit"
                  />
                </div>
              )}
              {type === 'time' && (
                <div>
                  <Label>Unit</Label>
                  <Input value="min" disabled />
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Time of day</Label>
              <Select value={timeOfDay} onValueChange={setTimeOfDay}>
                <SelectTrigger data-testid="habit-form-time-of-day"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="morning">Morning</SelectItem>
                  <SelectItem value="afternoon">Afternoon</SelectItem>
                  <SelectItem value="evening">Evening</SelectItem>
                  <SelectItem value="anytime">Anytime</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Schedule</Label>
              <Select value={scheduleType} onValueChange={setScheduleType}>
                <SelectTrigger data-testid="habit-form-schedule"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Every day</SelectItem>
                  <SelectItem value="weekdays">Weekdays</SelectItem>
                  <SelectItem value="weekends">Weekends</SelectItem>
                  <SelectItem value="custom">Specific days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {scheduleType === 'custom' && (
            <div>
              <Label className="block mb-2">Days</Label>
              <div className="flex flex-wrap gap-1.5">
                {DAYS.map((d) => (
                  <button
                    type="button"
                    key={d.v}
                    onClick={() => toggleDay(d.v)}
                    className={`h-9 px-3 rounded-full text-xs font-medium border transition-colors ${
                      scheduleDays.includes(d.v)
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-border text-muted-foreground hover:border-muted-foreground'
                    }`}
                    data-testid={`habit-form-day-${d.v}`}
                  >
                    {d.l}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="reminder">Reminder (optional)</Label>
            <div className="flex items-center gap-2">
              <Input
                id="reminder"
                type="time"
                value={reminderTime}
                onChange={(e) => setReminderTime(e.target.value)}
                className="max-w-[160px]"
                data-testid="habit-form-reminder"
              />
              {reminderTime && (
                <button
                  type="button"
                  onClick={() => setReminderTime('')}
                  className="text-xs text-muted-foreground hover:text-foreground"
                  data-testid="habit-form-reminder-clear"
                >
                  Clear
                </button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">Browser notification at this time on scheduled days. Enable notifications in Settings.</p>
          </div>

          <div>
            <Label htmlFor="category">Category (optional)</Label>
            <Select value={categoryId || 'none'} onValueChange={(v) => setCategoryId(v === 'none' ? '' : v)}>
              <SelectTrigger id="category" data-testid="habit-form-category"><SelectValue placeholder="No category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No category</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c.color }} />
                      {c.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1.5">Manage categories in Settings → Categories.</p>
          </div>

          <div>
            <Label className="block mb-2">Color</Label>
            <div className="flex flex-wrap gap-2">
              {HABIT_COLORS.map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => setColor(c)}
                  className={`h-7 w-7 rounded-full transition-transform ${color === c ? 'ring-2 ring-offset-2 ring-offset-background ring-primary scale-110' : ''}`}
                  style={{ backgroundColor: c }}
                  aria-label={`Color ${c}`}
                  data-testid={`habit-form-color-${c}`}
                />
              ))}
            </div>
          </div>

          <div>
            <Label className="block mb-2">Icon</Label>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
              {HABIT_ICONS.map((i) => {
                const I = LucideIcons[toIconKey(i)] || LucideIcons.Sparkles;
                return (
                  <button
                    type="button"
                    key={i}
                    onClick={() => setIcon(i)}
                    className={`h-9 w-9 rounded-lg border flex items-center justify-center transition-colors ${
                      icon === i ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-muted-foreground'
                    }`}
                    data-testid={`habit-form-icon-${i}`}
                    aria-label={`Icon ${i}`}
                  >
                    <I className="h-4 w-4" strokeWidth={1.6} />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="habit-form-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !name.trim()} data-testid="habit-form-submit" className="rounded-full">
              {saving ? 'Saving...' : initial?.id ? 'Save changes' : 'Create habit'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
